import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { authService } from '../services';
import { normalizeRole } from '../utils/rolePermissions';

const Sidebar = ({ collapsed, visible, onLogout }) => {
  const location = useLocation();
  const [expandedMenus, setExpandedMenus] = useState({});
  const [clickedMenu, setClickedMenu] = useState(null);
  const [clickedMenuPosition, setClickedMenuPosition] = useState({ top: 0 });
  const [userRole, setUserRole] = useState(null);
  const [favorites, setFavorites] = useState([]);

  // Get user role from localStorage
  useEffect(() => {
    const role = normalizeRole(authService.getUserRole());
    setUserRole(role);
  }, []);

  // Load favorites from localStorage
  useEffect(() => {
    const savedFavorites = localStorage.getItem('sidebarFavorites');
    if (savedFavorites) {
      try {
        setFavorites(JSON.parse(savedFavorites));
      } catch (error) {
        console.error('Error loading favorites:', error);
        setFavorites([]);
      }
    }
  }, []);

  // Save favorites to localStorage
  const saveFavorites = (newFavorites) => {
    localStorage.setItem('sidebarFavorites', JSON.stringify(newFavorites));
    setFavorites(newFavorites);
  };

  // Check if a link is favorited
  const isFavorited = (path) => {
    return favorites.some(fav => fav.path === path);
  };

  // Toggle favorite
  const toggleFavorite = (e, path, label, icon, parentIcon = null) => {
    e.preventDefault();
    e.stopPropagation();

    const existingIndex = favorites.findIndex(fav => fav.path === path);

    if (existingIndex !== -1) {
      // Remove from favorites
      const newFavorites = favorites.filter((_, index) => index !== existingIndex);
      saveFavorites(newFavorites);
    } else {
      // Add to favorites
      const newFavorite = {
        path,
        label,
        icon: parentIcon || icon
      };
      saveFavorites([...favorites, newFavorite]);
    }
  };

  // Define all navigation items with role requirements
  const allNavItems = [
    {
      path: '/dashboard',
      icon: 'bi bi-speedometer2',
      label: 'Dashboard',
      roles: ['admin'], // Only visible to admin
      submenu: [
        { path: '/dashboard/transaction-bi', label: 'Transaction Bi' },
        { path: '/dashboard/mapwise-bi', label: 'Mapwise Bi' },
        { path: '/dashboard/productwise-bi', label: 'Productwise Bi' }
      ]
    },
    {
      path: '/user-management',
      icon: 'bi bi-people',
      label: 'User Management',
      roles: ['admin'], // Only visible to admin
      submenu: [
        { path: '/user-management/super-distributor', label: 'Super Distributor' },
        { path: '/user-management/master-distributor', label: 'Master Distributor' },
        { path: '/user-management/distributor', label: 'Distributor' },
        { path: '/user-management/retailer', label: 'Retailer' },
        { path: '/user-management/retailer-approval', label: 'Retailer Approval' },
        { path: '/user-management/api-partner', label: 'API Partner' },
        { path: '/user-management/sales-force', label: 'Sales Force' },
        { path: '/user-management/support', label: 'Support' }
      ]
    },
    {
      path: '/top-up-request',
      icon: 'bi bi-cash-coin',
      label: 'Top Up Request',
      roles: ['admin'], // Only visible to admin
      exact: true
    },
    {
      path: '/transaction',
      icon: 'bi bi-receipt',
      label: 'Transaction',
      roles: ['admin'], // Only visible to admin
      submenu: [
        { path: '/transaction/all', label: 'All Transaction' },
        { path: '/transaction/pending', label: 'Pending Transaction' },
        { path: '/transaction/live', label: 'Live Transaction' },
        { path: '/transaction/search', label: 'Search Transaction' },
        { path: '/transaction/cron', label: 'Hold Transaction' }
      ]
    },
    {
      path: '/reports',
      icon: 'bi bi-file-earmark-bar-graph',
      label: 'Reports',
      roles: ['admin'], // Only visible to admin
      submenu: [
        { path: '/reports/summary-reports', label: 'Summary Reports' },
        { path: '/reports/all-transactions', label: 'All Transactions' },
        { path: '/reports/userwise-ledger', label: 'Userwise Ledger' },
        { path: '/reports/top-up-history', label: 'Top-Up History' },
        { path: '/reports/payin-reports', label: 'Payin Reports' },
        { path: '/reports/tds-reports', label: 'TDS Reports' },
        { path: '/reports/commission-reports', label: 'Commission Reports' }
      ]
    },
    {
      path: '/sales-force',
      icon: 'bi bi-briefcase',
      label: 'Sales Force',
      roles: ['admin'], // Only visible to admin
      submenu: [
        { path: '/sales-force/rsm', label: 'RSM' },
        { path: '/sales-force/asm', label: 'ASM' },
        { path: '/sales-force/tsm', label: 'TSM' }
      ]
    },
    {
      path: '/switching',
      icon: 'bi bi-arrow-left-right',
      label: 'Switching',
      roles: ['admin'], // Only visible to admin
      submenu: [
        { path: '/switching/userwise', label: 'UserWise Switching' },
        { path: '/switching/random', label: 'Random Switching' },
        { path: '/switching/denomination', label: 'Denomination Switch' },
        { path: '/switching/failure', label: 'Failure Switching' },
        { path: '/switching/pending', label: 'Pending Switching' },
        { path: '/switching/circle-wise', label: 'Circle Wise Switching' }
      ]
    },
    {
      path: '/settings',
      icon: 'bi bi-gear',
      label: 'Settings',
      roles: ['admin'], // Only visible to admin
      submenu: [
        { path: '/settings/services', label: 'Services' },
        { path: '/settings/operators', label: 'Operators' },
        { path: '/settings/bank-master', label: 'Bank Master' },
        { path: '/settings/low-balance-alert', label: 'Low Balance Alert' },
        { path: '/settings/notification', label: 'Announcement' },
        { path: '/settings/error-log', label: 'Error Log' },
        { path: '/settings/beneficiaries', label: 'Beneficiaries' },
        { path: '/settings/payout-beneficiaries', label: 'Payout Beneficiaries' },
        { path: '/settings/login-ips', label: "Login IP's" }
      ]
    },
    {
      path: '/api-provider',
      icon: 'bi bi-hdd-network',
      label: 'API Provider',
      roles: ['admin'], // Only visible to admin
      submenu: [
        { path: '/api-provider/list', label: 'API Provider' },
        { path: '/api-provider/commission', label: 'API Provider Commission' }
      ]
    },
    {
      path: '/commission-config',
      icon: 'bi bi-percent',
      label: 'Commission Config',
      roles: ['admin'], // Only visible to admin
      submenu: [
        { path: '/commission-config/plan', label: 'Plan' },
        { path: '/commission-config/slab', label: 'Slab' }
      ]
    },
    {
      path: '/device-order',
      icon: 'bi bi-phone',
      label: 'Device Order',
      roles: ['admin'], // Only visible to admin
      exact: true
    },
    {
      path: '/logout',
      icon: 'bi bi-box-arrow-right',
      label: 'Logout',
      roles: ['admin'],
      exact: true,
      isLogout: true
    },

    // ==========================================
    // SUPER DISTRIBUTOR NAVIGATION ITEMS
    // ==========================================
    {
      path: '/dashboard',
      icon: 'bi bi-speedometer2',
      label: 'Dashboard',
      roles: ['super_distributor'],
      exact: true
    },
    {
      path: '/user-management',
      icon: 'bi bi-people',
      label: 'User Management',
      roles: ['super_distributor'],
      submenu: [
        { path: '/user-management/master-distributor', label: 'Master Distributor' },
        { path: '/user-management/distributor', label: 'Distributor' },
        { path: '/user-management/retailer', label: 'Retailer' }
      ]
    },
    {
      path: '/top-up-request',
      icon: 'bi bi-cash-coin',
      label: 'Top Up Request',
      roles: ['super_distributor'],
      submenu: [
        { path: '/top-up-request/from-retailer', label: 'Top Up Request From Retailer' },
        { path: '/top-up-request/send', label: 'Send Top Up Request' }
      ]
    },
    {
      path: '/reports',
      icon: 'bi bi-file-earmark-bar-graph',
      label: 'Reports',
      roles: ['super_distributor'],
      submenu: [
        { path: '/reports/master-distributor-transactions', label: 'Master Distributor Transaction Report' },
        { path: '/reports/ledger', label: 'Ledger' },
        { path: '/reports/commission', label: 'Commission Reports' },
        { path: '/reports/top-up-history', label: 'Top Up History' },
        { path: '/reports/distributor-transactions', label: 'Distributor Transaction Report' },
        { path: '/reports/retailer-transactions', label: 'Retailer Transaction Report' },
        { path: '/reports/outstanding-reports', label: 'Outstanding Reports' },
        { path: '/reports/outstanding', label: 'Outstanding' }
      ]
    },
    {
      path: '/logout',
      icon: 'bi bi-box-arrow-right',
      label: 'Logout',
      roles: ['super_distributor'],
      exact: true,
      isLogout: true
    },

    // ==========================================
    // MASTER DISTRIBUTOR NAVIGATION ITEMS
    // ==========================================
    {
      path: '/dashboard',
      icon: 'bi bi-speedometer2',
      label: 'Dashboard',
      roles: ['master_distributor'],
      exact: true
    },
    {
      path: '/user-management',
      icon: 'bi bi-people',
      label: 'User Management',
      roles: ['master_distributor'],
      submenu: [
        { path: '/user-management/distributor', label: 'Distributor' },
        { path: '/user-management/retailer', label: 'Retailer' }
      ]
    },
    {
      path: '/top-up-request',
      icon: 'bi bi-cash-coin',
      label: 'Top Up Request',
      roles: ['master_distributor'],
      submenu: [
        { path: '/top-up-request/from-retailer', label: 'Top Up Request From Retailer' },
        { path: '/top-up-request/send', label: 'Send Top Up Request' }
      ]
    },
    {
      path: '/reports',
      icon: 'bi bi-file-earmark-bar-graph',
      label: 'Reports',
      roles: ['master_distributor'],
      submenu: [
        { path: '/reports/ledger', label: 'Ledger' },
        { path: '/reports/commission', label: 'Commission Reports' },
        { path: '/reports/top-up-history', label: 'Top Up History' },
        { path: '/reports/distributor-transactions', label: 'Distributor Transaction Report' },
        { path: '/reports/retailer-transactions', label: 'Retailer Transaction Report' },
        { path: '/reports/outstanding-reports', label: 'Outstanding Reports' },
        { path: '/reports/outstanding', label: 'Outstanding' }
      ]
    },
    {
      path: '/logout',
      icon: 'bi bi-box-arrow-right',
      label: 'Logout',
      roles: ['master_distributor'],
      exact: true,
      isLogout: true
    },

    // ==========================================
    // DISTRIBUTOR NAVIGATION ITEMS
    // ==========================================
    {
      path: '/dashboard',
      icon: 'bi bi-speedometer2',
      label: 'Dashboard',
      roles: ['distributor'],
      exact: true
    },
    {
      path: '/user-management',
      icon: 'bi bi-people',
      label: 'User Management',
      roles: ['distributor'],
      submenu: [
        { path: '/user-management/retailer', label: 'Retailer' }
      ]
    },
    {
      path: '/top-up-request',
      icon: 'bi bi-cash-coin',
      label: 'Top Up Request',
      roles: ['distributor'],
      submenu: [
        { path: '/top-up-request/from-retailer', label: 'From Retailer' },
        { path: '/top-up-request/send', label: 'Send Request' }
      ]
    },
    {
      path: '/reports',
      icon: 'bi bi-file-earmark-bar-graph',
      label: 'Reports',
      roles: ['distributor'],
      submenu: [
        { path: '/reports/ledger', label: 'Ledger' },
        { path: '/reports/commission', label: 'Commission Reports' },
        { path: '/reports/top-up-history', label: 'Top Up History' },
        { path: '/reports/retailer-transactions', label: 'Retailer Transactions' },
        { path: '/reports/outstanding', label: 'Outstanding Reports' },
        { path: '/reports/outstanding-summary', label: 'Outstanding Summary' },
        { path: '/reports/set-balance-limit', label: 'Set Balance Limit' }
      ]
    },
    {
      path: '/logout',
      icon: 'bi bi-box-arrow-right',
      label: 'Logout',
      roles: ['distributor'],
      exact: true,
      isLogout: true
    },

    // ==========================================
    // API PARTNER NAVIGATION ITEMS
    // ==========================================
    {
      path: '/dashboard',
      icon: 'bi bi-speedometer2',
      label: 'Dashboard',
      roles: ['api_partner'],
      exact: true
    },
    {
      path: '/top-up-request',
      icon: 'bi bi-cash-stack',
      label: 'Top Up Request',
      roles: ['api_partner'],
      exact: true
    },
    {
      path: '/white-listing',
      icon: 'bi bi-shield-lock',
      label: 'White Listing',
      roles: ['api_partner'],
      submenu: [
        { path: '/white-listing/webhook-url', label: 'Web Hook URL' },
        { path: '/white-listing/ip-white-listing', label: 'IP White Listing' }
      ]
    },
    {
      path: '/ledger',
      icon: 'bi bi-journal-richtext',
      label: 'Ledger',
      roles: ['api_partner'],
      exact: true
    },
    {
      path: '/commission-report',
      icon: 'bi bi-bar-chart-line',
      label: 'Commission Report',
      roles: ['api_partner'],
      exact: true
    },
    {
      path: '/my-commission',
      icon: 'bi bi-receipt',
      label: 'My Commission',
      roles: ['api_partner'],
      exact: true
    },
    {
      path: '/logout',
      icon: 'bi bi-box-arrow-right',
      label: 'Logout',
      roles: ['api_partner'],
      exact: true,
      isLogout: true
    },

    // ==========================================
    // RETAILER NAVIGATION ITEMS
    // ==========================================
    {
      path: '/dashboard',
      icon: 'bi bi-speedometer2',
      label: 'Dashboard',
      roles: ['retailer'],
      exact: true
    },
    // {
    //   path: '/upi-transfer',
    //   icon: 'bi bi-bank',
    //   label: 'UPI Transfer',
    //   rance RequestiReport
    //   exact: true
    // },
    {
      path: '/fund-transfer',
      icon: 'bi bi-cash-stack',
      label: 'Fund Transfer',
      roles: ['retailer'],
      exact: true
    },
    // {
    //   path: '/upi-collect',
    //   icon: 'bi bi-arrow-down-circle',
    //   label: 'UPI Collect',
    //   roles: ['retailer'],
    //   exact: true
    // },
    {
      path: '/aeps',
      icon: 'bi bi-fingerprint',
      label: 'AEPS',
      roles: ['retailer'],
      exact: true
    },
    {
      path: '/micro-atm-pos',
      icon: 'bi bi-credit-card-2-front',
      label: 'Micro ATM & POS',
      roles: ['retailer'],
      exact: true
    },
    {
      path: '/mobile-recharge',
      icon: 'bi bi-phone',
      label: 'Mobile Recharge',
      roles: ['retailer'],
      exact: true
    },
    {
      path: '/dth-recharge',
      icon: 'bi bi-tv',
      label: 'DTH Recharge',
      roles: ['retailer'],
      exact: true
    },
    // {
    //   path: '/loan',
    //   icon: 'bi bi-wallet2',
    //   label: 'Loan',
    //   roles: ['retailer'],
    //   exact: true
    // },
    {
      path: '/bill-payments',
      icon: 'bi bi-receipt-cutoff',
      label: 'Bill Payments',
      roles: ['retailer'],
      exact: true
    },
    {
      path: '/my-account',
      icon: 'bi bi-person-circle',
      label: 'My Account',
      roles: ['retailer'],
      submenu: [
        { path: '/my-account/balance-request', label: 'Balance Request' },
        { path: '/my-account/claim-refund', label: 'Claim Refund' },
        { path: '/my-account/payout-beneficiary', label: 'Payout Beneficiary' },
        { path: '/my-account/network-payment', label: 'Network Payment' }
      ]
    },
    {
      path: '/reports',
      icon: 'bi bi-file-earmark-bar-graph',
      label: 'Reports',
      roles: ['retailer'],
      submenu: [
        { path: '/reports/ledger-reports', label: 'Ledger' },
        { path: '/reports/commission-reports', label: 'Commission Reports' },
        { path: '/reports/payout-reports', label: 'Payin Reports' },
        { path: '/reports/bal-req-history', label: 'Balance Request Report' },
        { path: '/reports/outstanding-reports', label: 'Outstanding Reports' }
      ]
    },
    {
      path: '/logout',
      icon: 'bi bi-box-arrow-right',
      label: 'Logout',
      roles: ['retailer'],
      exact: true,
      isLogout: true
    },
    // Add more menu items here with their respective roles
  ];

  // Filter nav items based on user role
  const priorityLookup = {
    '/dashboard': 0,
    '/fund-transfer': 1,
    '/mobile-recharge': 2,
    '/bill-payments': 3,
    '/aeps': 4,
    '/dth-recharge': 5
  };

  const rolePriorityMap = {
    api_partner: {
      '/dashboard': 0,
      '/top-up-request': 1,
      '/white-listing': 2,
      '/ledger': 3,
      '/commission-report': 4,
      '/my-commission': 5,
      '/logout': 6
    },
    master_distributor: {
      '/dashboard': 0,
      '/user-management': 1,
      '/top-up-request': 2,
      '/reports': 3,
      '/logout': 4
    },
    super_distributor: {
      '/dashboard': 0,
      '/user-management': 1,
      '/top-up-request': 2,
      '/reports': 3,
      '/logout': 4
    },
    distributor: {
      '/dashboard': 0,
      '/user-management': 1,
      '/top-up-request': 2,
      '/reports': 3,
      '/logout': 4
    }
  };

  const getPriority = (path) => {
    if (!path) {
      return 5000;
    }
    if (rolePriorityMap[userRole]?.hasOwnProperty(path)) {
      return rolePriorityMap[userRole][path];
    }
    if (path === '/logout') {
      return 100000;
    }
    if (priorityLookup.hasOwnProperty(path)) {
      return priorityLookup[path];
    }
    return 5000;
  };

  const navItems = allNavItems
    .filter(item => {
      // If no roles specified, show to everyone
      if (!item.roles || item.roles.length === 0) {
        return true;
      }
      // Check if user's role is in the allowed roles
      return item.roles.includes(userRole);
    })
    .sort((a, b) => {
      const aPriority = getPriority(a.path);
      const bPriority = getPriority(b.path);

      if (aPriority !== bPriority) {
        return aPriority - bPriority;
      }

      return (a.label || '').localeCompare(b.label || '');
    });

  const toggleMenu = (menuPath) => {
    if (collapsed) return; // Don't expand menus when sidebar is collapsed

    setExpandedMenus(prev => {
      // If clicking on already expanded menu, close it
      if (prev[menuPath]) {
        return {};
      }
      // Otherwise, close all menus and open only the clicked one
      return { [menuPath]: true };
    });
  };

  const isActive = (path, exact = false) => {
    if (exact) {
      return location.pathname === path || location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  const isMenuExpanded = (menuPath) => {
    return expandedMenus[menuPath] || false;
  };

  const hasActiveSubmenu = (submenu) => {
    return submenu && submenu.some(item => location.pathname.startsWith(item.path));
  };

  const handleMenuClick = (menuPath, event) => {
    if (collapsed) {
      event.preventDefault();
      event.stopPropagation();
      const rect = event.currentTarget.getBoundingClientRect();
      setClickedMenuPosition({ top: rect.top });

      // Toggle submenu - close if already open, open if closed
      if (clickedMenu === menuPath) {
        setClickedMenu(null);
      } else {
        setClickedMenu(menuPath);
      }
    } else {
      // Normal behavior for expanded sidebar
      toggleMenu(menuPath);
    }
  };

  // Close submenu when clicking outside
  const handleDocumentClick = useCallback((event) => {
    if (collapsed && clickedMenu) {
      const submenu = document.querySelector('.nav-submenu-hover');
      const menuItem = event.target.closest('.nav-menu-group');

      if (!submenu?.contains(event.target) && !menuItem) {
        setClickedMenu(null);
      }
    }
  }, [collapsed, clickedMenu]);

  // Add document click listener
  useEffect(() => {
    document.addEventListener('click', handleDocumentClick);
    return () => {
      document.removeEventListener('click', handleDocumentClick);
    };
  }, [collapsed, clickedMenu]);

  return (
    <div className={`fintech-sidebar ${collapsed ? 'collapsed' : ''} ${visible ? 'show' : ''}`}>
      <div className="sidebar-header">
        <div className="sidebar-brand">
          <div className="brand-logo">
            <img
              src="/BrandLogo.svg"
              alt="Shakti E Solution Logo"
              className="sidebar-logo"
              onError={(e) => {
                e.target.style.display = 'none';
                const fallback = e.target.nextElementSibling;
                if (fallback) fallback.style.display = 'flex';
              }}
            />
            <div className="brand-icon" style={{ display: 'none' }}>
              Shakti E Solution
            </div>
          </div>
        </div>
      </div>

      <nav className="sidebar-nav">
        {/* Favorites Section */}
        {favorites.length > 0 && (
          <div className="favorites-section">
            {!collapsed && (
              <div className="section-header">
                <i className="bi bi-star-fill"></i>
                <span className="section-title">Favorites</span>
              </div>
            )}
            <div className="favorites-list">
              {favorites.map((favorite, index) => (
                <div key={index} className="nav-item">
                  <Link
                    to={favorite.path}
                    className={`nav-link ${isActive(favorite.path) ? 'active' : ''}`}
                    title={collapsed ? favorite.label : ''}
                  >
                    <i className={favorite.icon}></i>
                    {!collapsed && <span className="nav-label">{favorite.label}</span>}
                    {!collapsed && (
                      <i
                        className="bi bi-star-fill favorite-icon filled"
                        onClick={(e) => toggleFavorite(e, favorite.path, favorite.label, favorite.icon)}
                        title="Remove from favorites"
                      ></i>
                    )}
                  </Link>
                </div>
              ))}
            </div>
            {!collapsed && <div className="section-divider"></div>}
          </div>
        )}

        {/* Regular Navigation Items */}
        {navItems.map((item, index) => (
          <div key={index} className="nav-item">
            {item.submenu ? (
              <div className="nav-menu-group">
                <div
                  className={`nav-link nav-link-expandable ${isActive(item.path) || hasActiveSubmenu(item.submenu) || (collapsed && clickedMenu === item.path) ? 'active' : ''
                    }`}
                  onClick={(e) => handleMenuClick(item.path, e)}
                  title={collapsed ? item.label : ''}
                >
                  <i
                    className={item.icon}
                    style={{
                      transition: 'transform 0.3s ease',
                      transform: isMenuExpanded(item.path) ? 'rotate(20deg) scale(1.1)' : 'rotate(0deg) scale(1)'
                    }}
                  ></i>
                  {!collapsed && (
                    <>
                      <span className="nav-label">{item.label}</span>
                      <i
                        className={`bi ${isMenuExpanded(item.path) ? 'bi-chevron-down' : 'bi-chevron-right'} nav-chevron`}
                        style={{
                          transition: 'transform 0.3s ease'
                        }}
                      ></i>
                    </>
                  )}
                </div>

                {!collapsed && (
                  <div className={`nav-submenu ${isMenuExpanded(item.path) ? 'expanded' : ''}`}>
                    {item.submenu.map((subItem, subIndex) => (
                      <Link
                        key={subIndex}
                        to={subItem.path}
                        className={`nav-sublink ${isActive(subItem.path) ? 'active' : ''}`}
                      >
                        <i
                          className={`bi ${isFavorited(subItem.path) ? 'bi-star-fill' : 'bi-star'} favorite-icon ${isFavorited(subItem.path) ? 'filled' : ''}`}
                          onClick={(e) => toggleFavorite(e, subItem.path, subItem.label, item.icon)}
                          title={isFavorited(subItem.path) ? 'Remove from favorites' : 'Add to favorites'}
                        ></i>
                        <span className="nav-sublabel">{subItem.label}</span>
                      </Link>
                    ))}
                  </div>
                )}

                {collapsed && clickedMenu === item.path && (
                  <div
                    className="nav-submenu-hover"
                    style={{ top: clickedMenuPosition.top }}
                  >
                    <div className="submenu-header">
                      <span className="submenu-title">{item.label}</span>
                    </div>
                    {item.submenu.map((subItem, subIndex) => (
                      <Link
                        key={subIndex}
                        to={subItem.path}
                        className={`nav-sublink-hover ${isActive(subItem.path) ? 'active' : ''}`}
                        onClick={() => setClickedMenu(null)}
                      >
                        <span className="nav-sublabel">{subItem.label}</span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              item.isLogout ? (
                <div
                  className="nav-link"
                  onClick={onLogout}
                  style={{ cursor: 'pointer' }}
                  title={collapsed ? item.label : ''}
                >
                  <i className={item.icon}></i>
                  {!collapsed && <span className="nav-label">{item.label}</span>}
                </div>
              ) : (
                <Link
                  to={item.path}
                  className={`nav-link ${isActive(item.path, item.exact) ? 'active' : ''}`}
                  title={collapsed ? item.label : ''}
                >
                  <i className={item.icon}></i>
                  {!collapsed && <span className="nav-label">{item.label}</span>}
                  {!collapsed && !item.isLogout && (
                    <i
                      className={`bi ${isFavorited(item.path) ? 'bi-star-fill' : 'bi-star'} favorite-icon ${isFavorited(item.path) ? 'filled' : ''}`}
                      onClick={(e) => toggleFavorite(e, item.path, item.label, item.icon)}
                      title={isFavorited(item.path) ? 'Remove from favorites' : 'Add to favorites'}
                    ></i>
                  )}
                </Link>
              )
            )}
          </div>
        ))}
      </nav>
    </div>
  );
};

export default Sidebar;
